import { Cluster } from '@/pathfinding/hierarchical/Cluster';
import { EntrancePoint } from '@/pathfinding/hierarchical/EntrancePoint';
import { AbstractGraph, AbstractNode } from '@/pathfinding/hierarchical/AbstractGraph';

export class HierarchicalOverlayRenderer {
  static renderClusters(
    ctx: CanvasRenderingContext2D,
    clusters: Cluster[],
    cellSize: number,
    colors: Map<number, string>
  ): void {
    ctx.save();

    for (const cluster of clusters) {
      const bounds = cluster.bounds;
      const color = colors.get(cluster.id) || '#cccccc';

      const x = bounds.minCol * cellSize;
      const y = bounds.minRow * cellSize;
      const width = (bounds.maxCol - bounds.minCol + 1) * cellSize;
      const height = (bounds.maxRow - bounds.minRow + 1) * cellSize;

      ctx.fillStyle = this.addAlpha(color, 0.15);
      ctx.fillRect(x, y, width, height);

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);
    }

    ctx.restore();
  }

  static renderEntrancePoints(
    ctx: CanvasRenderingContext2D,
    entrances: EntrancePoint[],
    cellSize: number
  ): void {
    ctx.save();

    for (const entrance of entrances) {
      const center = entrance.getCenter();
      const x = center.col * cellSize + cellSize / 2;
      const y = center.row * cellSize + cellSize / 2;

      ctx.fillStyle = '#FFD700';
      ctx.strokeStyle = '#FFA500';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }

  static renderAbstractGraph(
    ctx: CanvasRenderingContext2D,
    graph: AbstractGraph,
    cellSize: number
  ): void {
    ctx.save();

    ctx.strokeStyle = '#6B7FFF';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);

    for (const edge of graph.getAllEdges()) {
      const fromX = edge.from.position.col * cellSize + cellSize / 2;
      const fromY = edge.from.position.row * cellSize + cellSize / 2;
      const toX = edge.to.position.col * cellSize + cellSize / 2;
      const toY = edge.to.position.row * cellSize + cellSize / 2;

      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.stroke();
    }

    ctx.setLineDash([]);

    ctx.fillStyle = '#6B7FFF';
    for (const node of graph.getAllNodes()) {
      const x = node.position.col * cellSize + cellSize / 2;
      const y = node.position.row * cellSize + cellSize / 2;

      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  static renderAbstractPath(
    ctx: CanvasRenderingContext2D,
    path: AbstractNode[],
    cellSize: number
  ): void {
    if (path.length < 2) return;

    ctx.save();

    ctx.strokeStyle = '#FF1493';
    ctx.lineWidth = 3;

    for (let i = 0; i < path.length - 1; i++) {
      const from = path[i];
      const to = path[i + 1];

      const fromX = from.position.col * cellSize + cellSize / 2;
      const fromY = from.position.row * cellSize + cellSize / 2;
      const toX = to.position.col * cellSize + cellSize / 2;
      const toY = to.position.row * cellSize + cellSize / 2;

      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.stroke();
    }

    ctx.fillStyle = '#FF1493';
    for (const node of path) {
      const x = node.position.col * cellSize + cellSize / 2;
      const y = node.position.row * cellSize + cellSize / 2;

      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private static addAlpha(color: string, alpha: number): string {
    if (color.startsWith('hsl')) {
      return color.replace(')', `, ${alpha})`).replace('hsl(', 'hsla(');
    }
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }
}
